import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  BarChart3,
  Edit3,
  Trash2,
  Eye,
  Download,
  Filter,
  TrendingUp,
  Calendar,
  Target,
  Users,
  Settings,
  AlertTriangle,
  Lightbulb
} from 'lucide-react';
import { getApiUrl, getAuthHeaders } from '../config/api';
import { useNotification } from '../contexts/NotificationContext';
import { useSubscription } from '../hooks/useSubscription';
import { FilterBuilder } from '../components/FilterBuilder';
import axios from 'axios';

export default function SavedReports() {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { hasFeature, isLoading: subscriptionLoading } = useSubscription();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedReportType, setSelectedReportType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingReport, setDeletingReport] = useState(null);
  const [showTemplateSuggestions, setShowTemplateSuggestions] = useState(false);
  const [templateSuggestions, setTemplateSuggestions] = useState([]);
  const [availableReportTypes, setAvailableReportTypes] = useState(['all', 'custom', 'barrel_racing', 'pole_bending', 'overview', 'comparison']);

  // Show loading while subscription data is being fetched
  if (subscriptionLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading subscription data...</p>
        </div>
      </div>
    );
  }

  // Check if user has access to insights feature
  if (!hasFeature('insights')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">Insights</h2>
          <p className="text-gray-600 mb-4">This feature is available with Showpremium subscription.</p>
          <button
            onClick={() => navigate('/subscription')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Upgrade to Showpremium
          </button>
        </div>
      </div>
    );
  }

  const checkAndUpdateAutoReports = useCallback(async (reports) => {
    try {
      // Get reports that have auto-update enabled
      const autoUpdateReports = reports.filter(report => report.auto_update);

      if (autoUpdateReports.length === 0) return;

      // Check for updates for each auto-update report
      const updatePromises = autoUpdateReports.map(async (report) => {
        try {
          const response = await axios.post(getApiUrl(`/api/saved-reports/${report.id}/auto-update`), {}, {
            headers: getAuthHeaders()
          });

          if (response.data.updated) {
            return {
              reportId: report.id,
              reportName: report.name,
              newResultsCount: response.data.newResultsCount
            };
          }
          return null;
        } catch (error) {
          console.error(`Error auto-updating report ${report.id}:`, error);
          return null;
        }
      });

      const results = await Promise.all(updatePromises);
      const updatedReports = results.filter(result => result !== null);

      if (updatedReports.length > 0) {
        // Refresh the reports list to show updated data
        const refreshResponse = await axios.get(getApiUrl('/api/saved-reports?include_stats=true'), {
          headers: getAuthHeaders()
        });
        setReports(refreshResponse.data);

        // Show notification about auto-updates
        const totalNewResults = updatedReports.reduce((sum, result) => sum + result.newResultsCount, 0);
        showNotification(
          `Auto-updated ${updatedReports.length} report${updatedReports.length > 1 ? 's' : ''} with ${totalNewResults} new result${totalNewResults > 1 ? 's' : ''}`,
          'success'
        );
      }
    } catch (error) {
      console.error('Error checking auto-update reports:', error);
      // Don't show error notification for auto-update failures to avoid spam
    }
  }, [showNotification]);

  // Helper function to apply report filters to results (same logic as SavedReportView)
  const applyReportFilters = useCallback((results, filters) => {
    if (!filters || Object.keys(filters).length === 0) {
      return results; // No filters set, show all results
    }

    const filteredResults = results.filter(result => {
      const resultData = result.result_data;
      let matches = true;
      

      // Helper function to evaluate filter conditions with nested groups
      const evaluateFilter = (filterConfig, getValue) => {
        if (!filterConfig) return true;

        // Handle legacy string format for backward compatibility
        if (typeof filterConfig === 'string') {
          return getValue().toLowerCase().includes(filterConfig.toLowerCase());
        }

        // Handle new object format with logic and groups
        if (filterConfig.logic && (filterConfig.conditions || filterConfig.groups)) {
          const evaluateCondition = (condition) => {
            const value = getValue().toLowerCase();
            switch (condition.type) {
              case 'contains':
                return value.includes(condition.value.toLowerCase());
              case 'startsWith':
                return value.startsWith(condition.value.toLowerCase());
              case 'endsWith':
                return value.endsWith(condition.value.toLowerCase());
              case 'exact':
                return value === condition.value.toLowerCase();
              case 'regex':
                try {
                  const regex = new RegExp(condition.value, 'i');
                  return regex.test(value);
                } catch (e) {
                  return false;
                }
              default:
                return false;
            }
          };

          const evaluateGroup = (group) => {
            const groupResults = (group.conditions || []).map(evaluateCondition);
            switch (group.logic) {
              case 'AND':
                return groupResults.every(result => result === true);
              case 'OR':
                return groupResults.some(result => result === true);
              case 'NOT':
                return !groupResults.some(result => result === true);
              default:
                return true;
            }
          };

          // Evaluate root level conditions
          const conditionResults = filterConfig.conditions ? 
            filterConfig.conditions.map(evaluateCondition) : [];
          
          // Evaluate groups
          const groupResults = filterConfig.groups ? 
            filterConfig.groups.map(evaluateGroup) : [];

          // Combine all results with connectors
          const allResults = [...conditionResults, ...groupResults];
          const connectors = filterConfig.connectors || [];

          if (allResults.length === 0) return true;
          if (allResults.length === 1) return allResults[0];

          // Apply connectors between results
          let result = allResults[0];
          for (let i = 1; i < allResults.length; i++) {
            const connector = connectors[i - 1] || { type: 'AND' };
            const nextResult = allResults[i];
            
            switch (connector.type) {
              case 'AND':
                result = result && nextResult;
                break;
              case 'OR':
                result = result || nextResult;
                break;
              case 'NOT':
                result = result && !nextResult;
                break;
              default:
                result = result && nextResult;
            }
          }

          return result;
        }

        return true;
      };
      
      // Show name filter
      if (filters.showName) {
        const showName = resultData?.ShowName || '';
        const showNameMatch = evaluateFilter(filters.showName, () => showName);
        if (!showNameMatch) matches = false;
      }

      // Event name filter
      if (filters.eventName) {
        const eventNameMatch = resultData?.events?.some(event =>
          evaluateFilter(filters.eventName, () => event.name || '')
        );
        if (!eventNameMatch) matches = false;
      }

      // Contestant name filter - use same logic as SavedReportView.jsx
      if (filters.contestantName) {
        const contestantMatch = resultData?.events?.some(event => {
          if (event.draws) {
            return event.draws.some(draw =>
              draw.contestants?.some(contestant =>
                evaluateFilter(filters.contestantName, () => contestant.contestantName || '')
              )
            );
          }
          return false;
        });
        if (!contestantMatch) matches = false;
      }

      // Time status filter (at draw level)
      if (filters.timeStatus) {
        const timeStatusMatch = resultData?.events?.some(event => {
          if (!event.draws || !Array.isArray(event.draws)) return false;
          
          return event.draws.some(draw => {
            const drawTimeStatus = draw.timeStatus || '';
            return evaluateFilter(filters.timeStatus, () => drawTimeStatus);
          });
        });
        if (!timeStatusMatch) matches = false;
      }

      return matches;
    });
    
    return filteredResults;
  }, []);

  // Helper function to filter events within a result for display purposes
  const filterEventsForDisplay = useCallback((result, filters) => {
    if (!filters || Object.keys(filters).length === 0) {
      return result; // No filters set, return result as-is
    }

    const resultData = result.result_data;
    
    if (!resultData?.events || !Array.isArray(resultData.events)) {
      return result; // No events to filter
    }

    // Helper function to evaluate filter conditions (same as applyReportFilters)
    const evaluateFilter = (filterConfig, getValue) => {
      if (!filterConfig) return true;

      // Handle legacy string format for backward compatibility
      if (typeof filterConfig === 'string') {
        return getValue().toLowerCase().includes(filterConfig.toLowerCase());
      }

      // Handle new object format with logic and groups
      if (filterConfig.logic && (filterConfig.conditions || filterConfig.groups)) {
        const evaluateCondition = (condition) => {
          const value = getValue().toLowerCase();
          switch (condition.type) {
            case 'contains':
              return value.includes(condition.value.toLowerCase());
            case 'startsWith':
              return value.startsWith(condition.value.toLowerCase());
            case 'endsWith':
              return value.endsWith(condition.value.toLowerCase());
            case 'exact':
              return value === condition.value.toLowerCase();
            case 'regex':
              try {
                const regex = new RegExp(condition.value, 'i');
                return regex.test(value);
              } catch (e) {
                return false;
              }
            default:
              return false;
          }
        };

        const evaluateGroup = (group) => {
          const groupResults = (group.conditions || []).map(evaluateCondition);
          switch (group.logic) {
            case 'AND':
              return groupResults.every(result => result === true);
            case 'OR':
              return groupResults.some(result => result === true);
            case 'NOT':
              return !groupResults.some(result => result === true);
            default:
              return true;
          }
        };

        // Evaluate root level conditions
        const conditionResults = filterConfig.conditions ? 
          filterConfig.conditions.map(evaluateCondition) : [];
        
        // Evaluate groups
        const groupResults = filterConfig.groups ? 
          filterConfig.groups.map(evaluateGroup) : [];

        // Combine all results with connectors
        const allResults = [...conditionResults, ...groupResults];
        const connectors = filterConfig.connectors || [];

        if (allResults.length === 0) return true;
        if (allResults.length === 1) return allResults[0];

        // Apply connectors between results
        let result = allResults[0];
        for (let i = 1; i < allResults.length; i++) {
          const connector = connectors[i - 1] || { type: 'AND' };
          const nextResult = allResults[i];
          
          switch (connector.type) {
            case 'AND':
              result = result && nextResult;
              break;
            case 'OR':
              result = result || nextResult;
              break;
            case 'NOT':
              result = result && !nextResult;
              break;
            default:
              result = result && nextResult;
          }
        }

        return result;
      }
      return true;
    };

    // Filter events based on the report's filter criteria
    const filteredEvents = resultData.events.map(event => {
      let eventMatches = true;

      // Event name filter
      if (filters.eventName) {
        const eventNameMatch = evaluateFilter(filters.eventName, () => event.name || '');
        if (!eventNameMatch) eventMatches = false;
      }

      // Time status filter (at draw level)
      if (filters.timeStatus && event.draws && Array.isArray(event.draws)) {
        const filteredDraws = event.draws.filter(draw => {
          const drawTimeStatus = draw.timeStatus || '';
          return evaluateFilter(filters.timeStatus, () => drawTimeStatus);
        });
        
        if (filteredDraws.length === 0) {
          eventMatches = false; // No matching draws, exclude event
        } else {
          // Replace draws with filtered ones
          event = { ...event, draws: filteredDraws };
        }
      }

      return eventMatches ? event : null;
    }).filter(Boolean); // Remove null events

    return {
      ...result,
      result_data: {
        ...resultData,
        events: filteredEvents
      }
    };
  }, []);

  // Calculate average final time from filtered results
  const calculateAverageFinalTime = useCallback((results) => {
    const allFinalTimes = [];
    
    results.forEach(result => {
      const resultData = result.result_data;
      if (resultData?.events && Array.isArray(resultData.events)) {
        resultData.events.forEach(event => {
          if (event.draws && Array.isArray(event.draws)) {
            event.draws.forEach(draw => {
              const finalTime = draw.finalTime;
              const timeStatus = draw.timeStatus;
              
              // Use the same validation logic as SavedReportView.jsx
              if (finalTime && 
                  finalTime !== '' && 
                  finalTime !== 'N/A' && 
                  timeStatus !== 'N/T') {
                const timeValue = parseFloat(finalTime);
                if (!isNaN(timeValue)) {
                  allFinalTimes.push(timeValue);
                }
              }
            });
          }
        });
      }
    });
    
    return allFinalTimes.length > 0 ? allFinalTimes.reduce((a, b) => a + b, 0) / allFinalTimes.length : null;
  }, []);

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(getApiUrl('/api/saved-reports?include_stats=true'), {
        headers: getAuthHeaders()
      });
      const reportsData = response.data;

      // Calculate client-side stats for each report
      const reportsWithClientStats = await Promise.all(
        reportsData.map(async (report) => {
          try {
            // Fetch report with its results
            const reportResponse = await axios.get(getApiUrl(`/api/saved-reports/${report.id}`), {
              headers: getAuthHeaders()
            });
            
            const allResults = reportResponse.data.results || [];
            
            // Apply filters to get filtered results
            const filteredResults = applyReportFilters(allResults, report.filters);
            
            // Apply event-level filtering to each result
            const fullyFilteredResults = filteredResults.map(result => 
              filterEventsForDisplay(result, report.filters)
            );
            
            // Calculate stats from filtered results
            const avgFinalTime = calculateAverageFinalTime(fullyFilteredResults);
            
            // Calculate total runs from filtered results
            let totalRuns = 0;
            fullyFilteredResults.forEach(result => {
              const resultData = result.result_data;
              if (resultData?.events && Array.isArray(resultData.events)) {
                resultData.events.forEach(event => {
                  if (event.draws && Array.isArray(event.draws)) {
                    totalRuns += event.draws.length;
                  }
                });
              }
            });
            
            return {
              ...report,
              stats: {
                ...report.stats,
                avg_final_time: avgFinalTime,
                total_runs: totalRuns
              }
            };
          } catch (error) {
            console.error(`Error fetching results for report ${report.id}:`, error);
            
            // Return report with explicit null values to show calculation failed
            return {
              ...report,
              stats: {
                ...report.stats,
                avg_final_time: null,
                total_runs: report.stats?.total_runs || 0
              }
            };
          }
        })
      );

      setReports(reportsWithClientStats);

      // Check for auto-update reports and update them
      await checkAndUpdateAutoReports(reportsWithClientStats);
    } catch (error) {
      showNotification('Failed to fetch reports', 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification, checkAndUpdateAutoReports, applyReportFilters, filterEventsForDisplay, calculateAverageFinalTime]);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await axios.get(getApiUrl('/api/saved-reports/templates/list'), {
        headers: getAuthHeaders()
      });
      setTemplates(response.data);
      
      // Extract unique report types from templates
      const templateTypes = [...new Set(response.data.map(template => template.report_type))];
      const allTypes = ['all', 'custom', ...templateTypes, 'barrel_racing', 'pole_bending', 'overview', 'comparison'];
      const uniqueTypes = [...new Set(allTypes)]; // Remove duplicates
      setAvailableReportTypes(uniqueTypes);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  }, []);

  const checkTemplateSuggestions = useCallback(async () => {
    try {
      const response = await axios.get(getApiUrl('/api/saved-reports/suggestions/templates'), {
        headers: getAuthHeaders()
      });
      
      if (response.data.has_suggestions && response.data.suggestions.length > 0) {
        setTemplateSuggestions(response.data.suggestions);
        setShowTemplateSuggestions(true);
      }
    } catch (error) {
      console.error('Error checking template suggestions:', error);
      // Don't show error notification for template suggestions to avoid spam
    }
  }, []);

  useEffect(() => {

    // Only proceed if subscription data has finished loading
    if (!subscriptionLoading) {
      if (hasFeature('insights')) {
        fetchReports();
        fetchTemplates();
        // Check for template suggestions after a short delay to let reports load
        setTimeout(() => {
          checkTemplateSuggestions();
        }, 1000);
      } else {
      }
    } else {
    }
  }, [subscriptionLoading, fetchReports, fetchTemplates, checkTemplateSuggestions]);

  const handleDeleteReport = async (reportId) => {
    const report = reports.find(r => r.id === reportId);
    setDeletingReport(report);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteReport = async () => {
    if (!deletingReport) return;

    try {
      await axios.delete(getApiUrl(`/api/saved-reports/${deletingReport.id}`), {
        headers: getAuthHeaders()
      });
      
      setReports(reports.filter(report => report.id !== deletingReport.id));
      showNotification('Report deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting report:', error);
      showNotification('Failed to delete report', 'error');
    } finally {
      setShowDeleteConfirm(false);
      setDeletingReport(null);
    }
  };

  const handleViewReport = (reportId) => {
    navigate(`/insights/${reportId}`);
  };

  const handleEditReport = (reportId) => {
    navigate(`/insights/${reportId}/edit`);
  };

  const handleCreateReportFromTemplate = async (suggestion) => {
    try {
      const reportData = {
        name: suggestion.template.name,
        description: suggestion.template.description,
        report_type: suggestion.template.report_type,
        filters: suggestion.template.default_filters || {},
        chart_config: suggestion.template.default_chart_config || {},
        auto_update: true // Enable auto-update for template-generated reports
      };

      const response = await axios.post(getApiUrl('/api/saved-reports'), reportData, {
        headers: getAuthHeaders()
      });

      const newReportId = response.data.reportId;

      // Add matching results to the new report
      if (suggestion.matching_results_count > 0) {
        // Get all matching results (we'll need to fetch them with the same filters)
        const resultsResponse = await axios.get(getApiUrl('/api/results'), {
          headers: getAuthHeaders()
        });

        // Apply the same filtering logic to get matching results
        const matchingResults = resultsResponse.data.filter(result => {
          const resultData = result.result_data;
          
          if (suggestion.template.default_filters) {
            const filters = suggestion.template.default_filters;
            
            // Event name filter
            if (filters.eventName) {
              const eventNameMatch = resultData?.events?.some(event => {
                const eventName = (event.name || '').toLowerCase();
                if (typeof filters.eventName === 'string') {
                  return eventName.includes(filters.eventName.toLowerCase());
                } else if (filters.eventName.conditions) {
                  return filters.eventName.conditions.some(condition => {
                    switch (condition.type) {
                      case 'contains':
                        return eventName.includes(condition.value.toLowerCase());
                      case 'startsWith':
                        return eventName.startsWith(condition.value.toLowerCase());
                      case 'endsWith':
                        return eventName.endsWith(condition.value.toLowerCase());
                      case 'exact':
                        return eventName === condition.value.toLowerCase();
                      default:
                        return false;
                    }
                  });
                }
                return false;
              });
              if (!eventNameMatch) return false;
            }
            
            // Show name filter
            if (filters.showName) {
              const showName = (resultData?.ShowName || '').toLowerCase();
              if (typeof filters.showName === 'string') {
                if (!showName.includes(filters.showName.toLowerCase())) return false;
              } else if (filters.showName.conditions) {
                const showNameMatch = filters.showName.conditions.some(condition => {
                  switch (condition.type) {
                    case 'contains':
                      return showName.includes(condition.value.toLowerCase());
                    case 'startsWith':
                      return showName.startsWith(condition.value.toLowerCase());
                    case 'endsWith':
                      return showName.endsWith(condition.value.toLowerCase());
                    case 'exact':
                      return showName === condition.value.toLowerCase();
                    default:
                      return false;
                  }
                });
                if (!showNameMatch) return false;
              }
            }
          }
          
          return true;
        });

        // Add matching results to the report
        if (matchingResults.length > 0) {
          const resultIds = matchingResults.map(r => r.id);
          await axios.post(getApiUrl(`/api/saved-reports/${newReportId}/results`), {
            result_ids: resultIds
          }, {
            headers: getAuthHeaders()
          });
        }
      }

      showNotification(
        `Created "${suggestion.template.name}" report with ${suggestion.matching_results_count} matching results`,
        'success'
      );

      // Refresh the reports list
      fetchReports();
    } catch (error) {
      console.error('Error creating report from template:', error);
      showNotification('Failed to create report from template', 'error');
    }
  };

  const filteredReports = reports
    .filter(report => {
      const matchesType = selectedReportType === 'all' || report.report_type === selectedReportType;
      const matchesSearch = report.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           report.description?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesType && matchesSearch;
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  const getReportTypeIcon = (reportType) => {
    switch (reportType) {
      case 'barrel_racing':
        return <Target className="w-5 h-5 text-orange-600" />;
      case 'pole_bending':
        return <TrendingUp className="w-5 h-5 text-green-600" />;
      case 'overview':
        return <BarChart3 className="w-5 h-5 text-blue-600" />;
      case 'comparison':
        return <Users className="w-5 h-5 text-purple-600" />;
      case 'custom':
        return <BarChart3 className="w-5 h-5 text-gray-600" />;
      default:
        // For any new types from templates, use a default icon
        return <BarChart3 className="w-5 h-5 text-gray-600" />;
    }
  };

  const getReportTypeLabel = (reportType) => {
    switch (reportType) {
      case 'barrel_racing':
        return 'Barrel Racing';
      case 'pole_bending':
        return 'Pole Bending';
      case 'overview':
        return 'Overview';
      case 'comparison':
        return 'Comparison';
      case 'custom':
        return 'Custom';
      default:
        // For any new types from templates, format them nicely
        return reportType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center">
              <Lightbulb className="w-8 h-8 mr-3 text-blue-600" />
              Insights
            </h1>
            <p className="text-gray-600 mt-1">Create and manage custom result reports with advanced analytics</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowTemplates(true)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors flex items-center"
            >
              <Settings className="w-4 h-4 mr-2" />
              Templates
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Report
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search Reports</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name or description..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="sm:w-48">
            <label className="block text-sm font-medium text-gray-700 mb-1">Report Type</label>
            <select
              value={selectedReportType}
              onChange={(e) => setSelectedReportType(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {availableReportTypes.map(type => (
                <option key={type} value={type}>
                  {type === 'all' ? 'All Types' : getReportTypeLabel(type)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Reports Grid */}
      {filteredReports.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
          <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Reports Found</h3>
          <p className="text-gray-600 mb-6">
            {searchTerm || selectedReportType !== 'all' 
              ? 'No reports match your current filters.' 
              : 'Create your first saved report to start tracking your performance over time.'
            }
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center mx-auto"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Your First Report
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
          {filteredReports.map((report) => (
            <div key={report.id} className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow">
              <div className="p-4 sm:p-6">
                <div className="flex items-start justify-between mb-3 sm:mb-4">
                  <div className="flex items-center min-w-0 flex-1">
                    {getReportTypeIcon(report.report_type)}
                    <div className="ml-2 sm:ml-3 min-w-0 flex-1">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{report.name}</h3>
                      <p className="text-xs sm:text-sm text-gray-500 truncate">{getReportTypeLabel(report.report_type)}</p>
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0 ml-2">
                    <button
                      onClick={() => handleViewReport(report.id)}
                      className="p-1.5 sm:p-2 text-gray-400 hover:text-blue-600 transition-colors"
                      title="View Report"
                    >
                      <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    </button>
                    <button
                      onClick={() => handleEditReport(report.id)}
                      className="p-1.5 sm:p-2 text-gray-400 hover:text-green-600 transition-colors"
                      title="Edit Report"
                    >
                      <Edit3 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteReport(report.id)}
                      className="p-1.5 sm:p-2 text-gray-400 hover:text-red-600 transition-colors"
                      title="Delete Report"
                    >
                      <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    </button>
                  </div>
                </div>

                {report.description && (
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">{report.description}</p>
                )}

                <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
                  <div className="text-center">
                    <div className="text-xl sm:text-2xl font-bold text-gray-900">{report.stats?.total_runs || report.result_count || 0}</div>
                    <div className="text-xs text-gray-500">Runs</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl sm:text-2xl font-bold text-gray-900">
                      {report.stats?.avg_final_time && typeof report.stats.avg_final_time === 'number' 
                        ? `${Number(report.stats.avg_final_time).toFixed(3)}s` 
                        : 'N/A'}
                    </div>
                    <div className="text-xs text-gray-500">Avg Time/Score</div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs text-gray-500 gap-2 sm:gap-0">
                  <div className="flex items-center">
                    <Calendar className="w-3 h-3 mr-1 flex-shrink-0" />
                    <span className="truncate">
                      {report.last_updated_at 
                        ? new Date(report.last_updated_at).toLocaleDateString()
                        : 'Never updated'
                      }
                    </span>
                  </div>
                  <div className="flex items-center">
                    <div className={`w-2 h-2 rounded-full mr-1 flex-shrink-0 ${
                      report.auto_update ? 'bg-green-500' : 'bg-gray-300'
                    }`}></div>
                    <span className="truncate">{report.auto_update ? 'Auto-update' : 'Manual'}</span>
                  </div>
                </div>

                <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200">
                  <button
                    onClick={() => handleViewReport(report.id)}
                    className="w-full px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center text-sm"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View Report
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Report Modal */}
      {showCreateModal && (
        <CreateReportModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchReports();
          }}
          templates={templates}
        />
      )}

      {/* Templates Modal */}
      {showTemplates && (
        <TemplatesModal
          onClose={() => setShowTemplates(false)}
          templates={templates}
        />
      )}

      {/* Template Suggestions Modal */}
      {showTemplateSuggestions && (
        <TemplateSuggestionsModal
          suggestions={templateSuggestions}
          onClose={() => {
            setShowTemplateSuggestions(false);
            setTemplateSuggestions([]);
          }}
          onAccept={(suggestion) => {
            handleCreateReportFromTemplate(suggestion);
            setShowTemplateSuggestions(false);
            setTemplateSuggestions([]);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deletingReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600 mr-3" />
              <h3 className="text-lg font-semibold">Confirm Delete</h3>
            </div>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete the report <span className="font-semibold">"{deletingReport.name}"</span>? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeletingReport(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteReport}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Create Report Modal Component
function CreateReportModal({ onClose, onSuccess, templates }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    report_type: 'custom',
    filters: {},
    chart_config: {},
    auto_update: false
  });
  const [loading, setLoading] = useState(false);
  const { showNotification } = useNotification();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      showNotification('Report name is required', 'error');
      return;
    }

    try {
      setLoading(true);
      await axios.post(getApiUrl('/api/saved-reports'), formData, {
        headers: getAuthHeaders()
      });
      
      showNotification('Report created successfully', 'success');
      onSuccess();
    } catch (error) {
      console.error('Error creating report:', error);
      showNotification('Failed to create report', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = (template) => {
    // Use the template's default filters and chart config
    let defaultFilters = template.default_filters || {};
    let defaultChartConfig = template.default_chart_config || {};
    
    // Convert old-style filters to new format if needed
    if (defaultFilters.eventName && typeof defaultFilters.eventName === 'string') {
      // Convert old format: {"eventName": "barrel"} 
      // To new format: {"eventName": {"logic": "AND", "conditions": [{"type": "contains", "value": "barrel"}]}}
      const eventNameValue = defaultFilters.eventName;
      defaultFilters = {
        ...defaultFilters,
        eventName: {
          logic: 'AND',
          conditions: [{ type: 'contains', value: eventNameValue }]
        }
      };
    }
    
    // Ensure chart config has bestTimeType for time-based events
    if (template.report_type === 'barrel_racing' || template.report_type === 'pole_bending') {
      defaultChartConfig = {
        ...defaultChartConfig,
        bestTimeType: 'lowest'
      };
    }
    
    setFormData({
      ...formData,
      name: template.name,
      description: template.description,
      report_type: template.report_type,
      filters: defaultFilters,
      chart_config: defaultChartConfig
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Create New Report</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Template Selection */}
          {templates.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Start with a Template</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {templates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => handleTemplateSelect(template)}
                    className="p-3 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left"
                  >
                    <div className="font-medium text-gray-900">{template.name}</div>
                    <div className="text-sm text-gray-500 mt-1">{template.description}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Report Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter report name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={3}
                placeholder="Describe what this report tracks"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Report Type</label>
              <select
                value={formData.report_type}
                onChange={(e) => {
                  const newType = e.target.value;
                  let newFilters = { ...formData.filters };
                  
                  // Set default filters based on report type using new format
                  if (newType === 'barrel_racing') {
                    newFilters.eventName = {
                      logic: 'AND',
                      conditions: [{ type: 'contains', value: 'barrel' }]
                    };
                  } else if (newType === 'pole_bending') {
                    newFilters.eventName = {
                      logic: 'AND',
                      conditions: [{ type: 'contains', value: 'pole' }]
                    };
                  } else if (newType === 'overview') {
                    // Clear event name filter for overview
                    delete newFilters.eventName;
                  }
                  
                  setFormData({ 
                    ...formData, 
                    report_type: newType,
                    filters: newFilters
                  });
                }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="custom">Custom</option>
                <option value="barrel_racing">Barrel Racing</option>
                <option value="pole_bending">Pole Bending</option>
                <option value="overview">Overview</option>
                <option value="comparison">Comparison</option>
              </select>
            </div>

            {/* Advanced Filters */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-700">Filters</h4>
              <p className="text-xs text-gray-500">Set criteria for automatically including new results using AND, OR, NOT logic</p>
              
              <div className="space-y-4">
                {/* Show Name Filter */}
                <FilterBuilder
                  label="Show Name"
                  filterKey="showName"
                  filters={formData.filters}
                  onUpdate={(newFilters) => setFormData({ ...formData, filters: newFilters })}
                  placeholder="e.g., Eburg Rodeo, State Fair"
                />
                
                {/* Event Name Filter */}
                <FilterBuilder
                  label="Event Name"
                  filterKey="eventName"
                  filters={formData.filters}
                  onUpdate={(newFilters) => setFormData({ ...formData, filters: newFilters })}
                  placeholder="e.g., Barrels, Poles, etc."
                />
                
                {/* Contestant Name Filter */}
                <FilterBuilder
                  label="Contestant Name"
                  filterKey="contestantName"
                  filters={formData.filters}
                  onUpdate={(newFilters) => setFormData({ ...formData, filters: newFilters })}
                  placeholder="e.g., John Smith"
                />
                
                {/* Stock Name Filter */}
                <FilterBuilder
                  label="Stock Name"
                  filterKey="stockName"
                  filters={formData.filters}
                  onUpdate={(newFilters) => setFormData({ ...formData, filters: newFilters })}
                  placeholder="e.g., horse name, bull name"
                />
                
                {/* Position Filter */}
                <FilterBuilder
                  label="Position"
                  filterKey="position"
                  filters={formData.filters}
                  onUpdate={(newFilters) => setFormData({ ...formData, filters: newFilters })}
                  placeholder="e.g., 1, 2, 3 or first, second"
                />
                
                {/* Time Status Filter */}
                <FilterBuilder
                  label="Time Status"
                  filterKey="timeStatus"
                  filters={formData.filters}
                  onUpdate={(newFilters) => setFormData({ ...formData, filters: newFilters })}
                  placeholder="e.g., OK, N/T, T/O, Vet"
                />
              </div>
            </div>

            {/* Chart Configuration */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-700">Chart Configuration</h4>
              <p className="text-xs text-gray-500">Configure how data will be displayed in chart view</p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Chart Type</label>
                  <select
                    value={formData.chart_config?.chartType || 'line'}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      chart_config: { 
                        ...formData.chart_config, 
                        chartType: e.target.value 
                      }
                    })}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="line">Line Chart</option>
                    <option value="bar">Bar Chart</option>
                    <option value="scatter">Scatter Plot</option>
                    <option value="area">Area Chart</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Y-Axis (Data)</label>
                  <select
                    value={formData.chart_config?.yAxis || 'time'}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      chart_config: { 
                        ...formData.chart_config, 
                        yAxis: e.target.value 
                      }
                    })}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="time">Final Time</option>
                    <option value="points">Points</option>
                    <option value="earnings">Earnings</option>
                    <option value="position">Position</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs text-gray-600 mb-1">X-Axis (Timeline)</label>
                  <select
                    value={formData.chart_config?.xAxis || 'date'}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      chart_config: { 
                        ...formData.chart_config, 
                        xAxis: e.target.value 
                      }
                    })}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="date">Date</option>
                    <option value="show">Show</option>
                    <option value="event">Event</option>
                    <option value="index">Sequence</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Best Time</label>
                  <select
                    value={formData.chart_config?.bestTimeType || 'lowest'}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      chart_config: { 
                        ...formData.chart_config, 
                        bestTimeType: e.target.value 
                      }
                    })}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="lowest">Lowest (Barrel Racing, Pole Bending)</option>
                    <option value="highest">Highest (Bull Riding, etc.)</option>
                  </select>
                </div>
                
              </div>
              
              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.chart_config?.showTrendLine || false}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      chart_config: { 
                        ...formData.chart_config, 
                        showTrendLine: e.target.checked 
                      }
                    })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-xs text-gray-700">Show trend line</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.chart_config?.showAverage || false}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      chart_config: { 
                        ...formData.chart_config, 
                        showAverage: e.target.checked 
                      }
                    })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-xs text-gray-700">Show average line</span>
                </label>
              </div>
            </div>

            <div className="flex items-center space-x-6">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.auto_update}
                  onChange={(e) => setFormData({ ...formData, auto_update: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Auto-update with new results</span>
              </label>

            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create Report'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// Templates Modal Component
function TemplatesModal({ onClose, templates }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Report Templates</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {templates.map((template) => (
              <div key={template.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900">{template.name}</h3>
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                    {template.report_type.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                <p className="text-gray-600 text-sm mb-4">{template.description}</p>
                <div className="text-xs text-gray-500">
                  Created: {new Date(template.created_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end pt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Template Suggestions Modal Component
function TemplateSuggestionsModal({ suggestions, onClose, onAccept }) {
  const getReportTypeIcon = (reportType) => {
    switch (reportType) {
      case 'barrel_racing':
        return <Target className="w-5 h-5 text-orange-600" />;
      case 'pole_bending':
        return <TrendingUp className="w-5 h-5 text-green-600" />;
      case 'overview':
        return <BarChart3 className="w-5 h-5 text-blue-600" />;
      case 'comparison':
        return <Users className="w-5 h-5 text-purple-600" />;
      case 'custom':
        return <BarChart3 className="w-5 h-5 text-gray-600" />;
      default:
        // For any new types from templates, use a default icon
        return <BarChart3 className="w-5 h-5 text-gray-600" />;
    }
  };

  const getReportTypeLabel = (reportType) => {
    switch (reportType) {
      case 'barrel_racing':
        return 'Barrel Racing';
      case 'pole_bending':
        return 'Pole Bending';
      case 'overview':
        return 'Overview';
      case 'comparison':
        return 'Comparison';
      default:
        return 'Custom';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[80]">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <Lightbulb className="w-6 h-6 text-blue-600 mr-3" />
              <h2 className="text-xl font-semibold text-gray-900">Report Suggestions</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="mb-4">
            <p className="text-gray-600">
              We found {suggestions.length} template{suggestions.length > 1 ? 's' : ''} that match your existing results. 
              Would you like to create reports for any of these?
            </p>
          </div>

          <div className="space-y-4 mb-6">
            {suggestions.map((suggestion, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center">
                    {getReportTypeIcon(suggestion.template.report_type)}
                    <div className="ml-3">
                      <h3 className="text-lg font-semibold text-gray-900">{suggestion.template.name}</h3>
                      <p className="text-sm text-gray-500">{getReportTypeLabel(suggestion.template.report_type)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-blue-600">{suggestion.matching_results_count}</div>
                    <div className="text-xs text-gray-500">matching results</div>
                  </div>
                </div>

                <p className="text-gray-600 text-sm mb-3">{suggestion.template.description}</p>

                {suggestion.sample_results.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 mb-2">Sample results:</p>
                    <div className="space-y-1">
                      {suggestion.sample_results.map((result, resultIndex) => (
                        <div key={resultIndex} className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">
                          {result.show_name} - {result.first_event_name}
                          {result.show_start_date && (
                            <span className="text-gray-400 ml-2">
                              ({new Date(result.show_start_date).toLocaleDateString()})
                            </span>
                          )}
                        </div>
                      ))}
                      {suggestion.matching_results_count > 3 && (
                        <div className="text-xs text-gray-400 italic">
                          ... and {suggestion.matching_results_count - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex justify-end">
                  <button
                    onClick={() => onAccept(suggestion)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Report
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-between pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Maybe Later
            </button>
            <div className="text-sm text-gray-500 flex items-center">
              <Lightbulb className="w-4 h-4 mr-1" />
              These reports will auto-update with new matching results
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
